import { get } from 'svelte/store';
import { user } from '$lib/stores/user/store';
import { AOFL_PRODUCT_UUIDS } from '$lib/utils/constants';
import { USER_TYPE_ABBREVIATIONS } from '$lib/stores/user/constants';

/**
 * Safely returns the aoflAccounts array from the user store.
 * @returns {Array<import('$lib/stores/user/store').AoflAccount>}
 */
const getAccounts = () => {
	const u = get(user) || {};
	return Array.isArray(u.aoflProducts) ? u.aoflProducts : [];
};

/**
 * Returns true if the user's account originated from the 1.0 product.
 * @returns {boolean}
 */
export const is1_0OriginatingUser = () => {
	const accounts = getAccounts();
	return accounts.some(
		(a) => a.aoflProductUuid === AOFL_PRODUCT_UUIDS.ABCMOUSE && a.originating === true
	);
};

/**
 * Returns true if the user's account originated from the 2.0 product.
 * @returns {boolean}
 */
export const is2_0OriginatingUser = () => {
	const accounts = getAccounts();
	return accounts.some(
		(a) => a.aoflProductUuid === AOFL_PRODUCT_UUIDS.ABCMOUSE_2_0 && a.originating === true
	);
};

/**
 * @returns {boolean}
 */
export const is1_0ActiveSubscriber = () => {
	const accounts = getAccounts();
	return accounts.some(
		(a) => a.aoflProductUuid === AOFL_PRODUCT_UUIDS.ABCMOUSE && a.activeSubscription === true
	);
};

/**
 * @returns {boolean}
 */
export const is2_0ActiveSubscriber = () => {
	const accounts = getAccounts();
	return accounts.some(
		(a) => a.aoflProductUuid === AOFL_PRODUCT_UUIDS.ABCMOUSE_2_0 && a.activeSubscription === true
	);
};

/**
 * Returns true if the user has any shared services subscription, whether it is a primary or auxiliary subscription
 * @returns {boolean}
 */
export const hasAnySharedServicesSubscription = () => {
	const accounts = getAccounts();
	const ssuValues = Object.values(USER_TYPE_ABBREVIATIONS).filter((v) => v.startsWith('ssu'));
	return accounts.some((a) => ssuValues.includes(a.accountType) && a.activeSubscription === true);
};

/**
 * Shared Services user if account_type matches one of the SSU variants
 * @returns {boolean}
 */
export const isSharedServicesUser = () => {
	const accounts = getAccounts();
	const ssuValues = Object.values(USER_TYPE_ABBREVIATIONS).filter((v) => v.startsWith('ssu'));
	return accounts.some((a) => ssuValues.includes(a.accountType));
};

/**
 * Legacy 1.0 user if a 1.0 account exists and account_type matches one of the OSU variants
 * @returns {boolean}
 */
export const is1_0LegacyUser = () => {
	const accounts = getAccounts();
	const osuValues = Object.values(USER_TYPE_ABBREVIATIONS).filter((v) => v.startsWith('osu'));
	return accounts.some(
		(a) => a.aoflProductUuid === AOFL_PRODUCT_UUIDS.ABCMOUSE && osuValues.includes(a.accountType)
	);
};

/**
 * Convenience helpers
 * @returns {boolean}
 */
export const has1_0Account = () => {
	const accounts = getAccounts();
	return accounts.some((a) => a.aoflProductUuid === AOFL_PRODUCT_UUIDS.ABCMOUSE);
};

/**
 * Returns true if the user has a 2.0 account.
 * @returns {boolean}
 */
export const has2_0Account = () => {
	const accounts = getAccounts();
	return accounts.some((a) => a.aoflProductUuid === AOFL_PRODUCT_UUIDS.ABCMOUSE_2_0);
};

/**
 * Returns the 1.0 account if it exists, otherwise null.
 * @returns {import('$lib/stores/user/store').AoflAccount | null}
 */
export const get1_0Account = () => {
	if (!has1_0Account()) {
		return null;
	}
	const accounts = getAccounts();
	return accounts.find((a) => a.aoflProductUuid === AOFL_PRODUCT_UUIDS.ABCMOUSE) ?? null;
};

/**
 * Returns the 2.0 account if it exists, otherwise null.
 * @returns {import('$lib/stores/user/store').AoflAccount | null}
 */
export const get2_0Account = () => {
	if (!has2_0Account()) {
		return null;
	}
	const accounts = getAccounts();
	return accounts.find((a) => a.aoflProductUuid === AOFL_PRODUCT_UUIDS.ABCMOUSE_2_0) ?? null;
};

/**
 * Returns true if the user should see 1.0 versions of the post-authentication flows.
 * This can be true even if the user has a 2.0 account, as there are scenarions where we'd still want them to see the 1.0 flows
 * @returns {boolean}
 */
export const shouldUse1_0PostAuthPath = () => {
	return is1_0ActiveSubscriber() || is1_0OriginatingUser();
};

/**
 * Returns true if the user should see 2.0 versions of the post-authentication flows.
 * This can be true even if the user has a 1.0 account, as there are scenarions where we'd still want them to see the 2.0 flows
 * @returns {boolean}
 */
export const shouldUse2_0PostAuthPath = () => {
	return !shouldUse1_0PostAuthPath();
};

/**
 * Returns true if the user should see 2.0 versions of the post-authentication flows for 1.0 originating users.
 * This is a special case where we want to show the 2.0 flows to 1.0 originating users who have a 2.0 subscription.
 * @returns {boolean}
 */
export const shouldUse2_0PostAuthPathFor1_0OriginatingUser = () => {
	return is1_0OriginatingUser() && is2_0ActiveSubscriber();
};
